// src/pages/Home.tsx
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../firebase/firebaseConfig";
import { getInvoicesByDateRange } from "../firebase/invoice/getInvoices";
import { getInvoiceByNumber } from "../firebase/invoice/getInvoiceByNumber";
import { getReturnsByDateRange } from "../firebase/returns/getReturnsByDateRange";
import { InvoiceData, LineItem } from "../models/invoice";
import {
	Box,
	Button,
	Card,
	CardActionArea,
	Collapse,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	Grid,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	SelectChangeEvent,
	Stack,
	Switch,
	Typography
} from "@mui/material";
import {
	Add,
	Inventory2,
	ReceiptLong,
	Refresh,
	Search,
	Settings
} from "@mui/icons-material";
import { CreditInvoices } from "../components/Invoice/CreditInvoices";
import { ConfirmDialog } from "../components/Shared/ConfirmDialog";
import { RecentInvoiceAndReturns } from "../components/Reports/InvoiceAndReturns/RecentInvoiceAndReturns";
import { useAuth } from "../context/AuthContext";
import { useThemeMode } from "../context/ThemeModeContext";
import { useUserSettings } from "../context/UserSettingsContext";

const quickActions = [
	{
		title: "Create invoice",
		icon: <ReceiptLong />,
		path: "/create-invoice",
		hint: "N"
	},
	{
		title: "Search invoices",
		icon: <Search />,
		path: "/search-invoice",
		hint: "⌕"
	},
	{
		title: "Inventory: Add item",
		icon: <Add />,
		path: "/add-item",
		hint: "I"
	},
	{
		title: "Inventory: All items",
		icon: <Inventory2 />,
		path: "/items",
		hint: "A"
	}
] as const;

export const Home: React.FC = () => {
	const navigate = useNavigate();
	const { user } = useAuth();
	const { mode, toggleMode } = useThemeMode();
	const { maskProfit, setMaskProfit } = useUserSettings();
	const [reportRange, setReportRange] = useState("today");
	const [logoutConfirmOpen, setLogoutConfirmOpen] = useState(false);
	const [outstandingOpen, setOutstandingOpen] = useState(false);
	const [totalOutstanding, setTotalOutstanding] = useState(0);
	const [settingsOpen, setSettingsOpen] = useState(false);
	const [todaySales, setTodaySales] = useState(0);
	const [todayProfit, setTodayProfit] = useState(0);
	const [weeklySales, setWeeklySales] = useState(0);
	const [overviewLoading, setOverviewLoading] = useState(true);

	const startDateToday = useMemo(
		() => new Date(new Date().setHours(0, 0, 0, 0)),
		[]
	);
	const endDateToday = useMemo(
		() => new Date(new Date().setHours(23, 59, 59, 999)),
		[]
	);
	const startDateLastWeek = useMemo(() => {
		const d = new Date();
		d.setDate(d.getDate() - 6);
		d.setHours(0, 0, 0, 0);
		return d;
	}, []);

	const calculateTotalSellPrice = (items: LineItem[]) => {
		return items.reduce(
			(total, item) => total + item.sellPrice * item.quantity,
			0
		);
	};

	const calculateTotalPurchasePrice = (items: LineItem[]) => {
		return items.reduce(
			(total, item) => total + (item.purchasePrice || 0) * item.quantity,
			0
		);
	};

	const loadSalesOverview = useCallback(
		async (rangeStart: Date, rangeEnd: Date) => {
			const startDate = rangeStart.toLocaleDateString("en-CA");
			const endDate = rangeEnd.toLocaleDateString("en-CA");
			const invoicesObj =
				(await getInvoicesByDateRange(startDate, endDate)) || {};
			const returnsInRange =
				(await getReturnsByDateRange(startDate, endDate)) || [];

			let sale = 0;
			let profit = 0;
			Object.values(invoicesObj).forEach((invoice: InvoiceData) => {
				const invoiceSale = calculateTotalSellPrice(invoice.items);
				sale += invoiceSale;
				const invoicePurchase = calculateTotalPurchasePrice(
					invoice.items
				);
				profit += invoiceSale - invoicePurchase;
			});

			let totalReturnAmount = 0;
			let totalReturnProfitLoss = 0;
			for (const ret of returnsInRange) {
				const invoice = await getInvoiceByNumber(ret.invoiceNumber);
				let returnAmount = ret.amount * ret.quantity;
				totalReturnAmount += returnAmount;
				let profitLoss = 0;
				if (invoice) {
					const returnedItem = invoice.items.find(
						(item) => item.lineItemId === ret.lineItemId
					);
					if (returnedItem) {
						const purchasePrice = returnedItem.purchasePrice || 0;
						profitLoss =
							returnAmount - purchasePrice * ret.quantity;
					}
				}
				totalReturnProfitLoss += profitLoss;
			}

			return {
				sale: sale - totalReturnAmount,
				profit: profit - totalReturnProfitLoss
			};
		},
		[]
	);

	useEffect(() => {
		let isActive = true;

		const fetchOverview = async () => {
			setOverviewLoading(true);
			try {
				const [todayStats, weeklyStats] = await Promise.all([
					loadSalesOverview(startDateToday, endDateToday),
					loadSalesOverview(startDateLastWeek, endDateToday)
				]);

				if (!isActive) return;
				setTodaySales(todayStats.sale);
				setTodayProfit(todayStats.profit);
				setWeeklySales(weeklyStats.sale);
				setTotalOutstanding(0);
			} catch (error) {
				console.error("Failed to load sales overview:", error);
				if (isActive) {
					setTodaySales(0);
					setTodayProfit(0);
					setWeeklySales(0);
					setTotalOutstanding(0);
				}
			} finally {
				if (isActive) {
					setOverviewLoading(false);
				}
			}
		};

		void fetchOverview();
		return () => {
			isActive = false;
		};
	}, [endDateToday, loadSalesOverview, startDateLastWeek, startDateToday]);

	const handleLogoutConfirm = async () => {
		try {
			await auth.signOut();
			setLogoutConfirmOpen(false);
			navigate("/login", { replace: true });
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	const handleLogoutCancel = () => {
		setLogoutConfirmOpen(false);
	};

	const handleRefresh = () => {
		window.location.reload();
	};

	const handleMaskProfitToggle = (checked: boolean) => {
		setMaskProfit(checked);
	};

	const handleReportRangeChange = (event: SelectChangeEvent) => {
		setReportRange(event.target.value);
	};
	const reportDays =
		reportRange === "5d"
			? 4
			: reportRange === "7d"
				? 6
				: reportRange === "15d"
					? 14
					: reportRange === "30d"
						? 29
						: 0;
	const username = user?.displayName ?? user?.email ?? "User";
	const formatCurrency = (value: number) =>
		new Intl.NumberFormat("en-IN", {
			style: "currency",
			currency: "INR",
			maximumFractionDigits: 2
		}).format(value);

	return (
		<Box
			sx={{
				minHeight: "100vh",
				display: "flex",
				bgcolor: "background.default",
				color: "text.primary"
			}}
		>
			<Box
				component="main"
				sx={{
					flex: 1,
					minWidth: 0,
					px: { xs: 2, md: 3, lg: 4 },
					py: { xs: 2, md: 3 }
				}}
			>
				<Box
					sx={{
						display: "flex",
						justifyContent: "space-between",
						alignItems: "center",
						mb: 3,
						gap: 2
					}}
				>
					<Box
						sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
					>
						<Box>
							<Typography
								variant="h4"
								sx={{
									fontWeight: 700,
									letterSpacing: "-0.04em"
								}}
							>
								Welcome, {username}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Here’s your store snapshot for today.
							</Typography>
						</Box>
					</Box>
					<Stack direction="row" spacing={1}>
						<IconButton
							onClick={handleRefresh}
							sx={{
								bgcolor: "primary.main",
								color: "white",
								borderRadius: 2,
								"&:hover": { bgcolor: "primary.dark" }
							}}
						>
							<Refresh />
						</IconButton>
						<Button
							variant="outlined"
							startIcon={<Settings />}
							onClick={() => setSettingsOpen(true)}
						>
							Settings
						</Button>
						<Button
							variant="contained"
							onClick={() => navigate("/create-invoice")}
						>
							＋ Create invoice
						</Button>
					</Stack>
				</Box>

				<Paper
					sx={{
						display: { xs: "none", md: "block" },
						p: { xs: 2, md: 3 },
						mb: 3,
						borderRadius: 3,
						bgcolor: "background.paper"
					}}
				>
					<Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
						Quick actions
					</Typography>
					<Grid container spacing={1.5}>
						{quickActions.map((action) => (
							<Grid
								key={action.title}
								size={{ xs: 12, sm: 6, md: 3 }}
							>
								<Card
									variant="outlined"
									sx={{
										height: "100%",
										borderColor: "divider",
										bgcolor: "background.paper"
									}}
								>
									<CardActionArea
										onClick={() => {
											switch (action.path) {
												case "/create-invoice":
													navigate("/create-invoice");
													break;
												case "/search-invoice":
													navigate("/search-invoice");
													break;
												case "/add-item":
													navigate("/add-item");
													break;
												case "/items":
													navigate("/items");
													break;
												default:
													navigate("/");
											}
										}}
										sx={{ height: "100%", p: 1.5 }}
									>
										<Box
											sx={{
												display: "flex",
												alignItems: "center",
												justifyContent: "space-between",
												mb: 1
											}}
										>
											<Box
												sx={{
													color: "primary.main",
													display: "flex",
													alignItems: "center"
												}}
											>
												{action.icon}
											</Box>
											<Typography
												variant="caption"
												color="text.secondary"
											>
												{action.hint}
											</Typography>
										</Box>
										<Typography
											variant="subtitle1"
											fontWeight={700}
										>
											{action.title}
										</Typography>
									</CardActionArea>
								</Card>
							</Grid>
						))}
					</Grid>
				</Paper>

				<Paper
					sx={{
						p: { xs: 2, md: 3 },
						mb: 3,
						borderRadius: 3,
						bgcolor: "background.paper"
					}}
				>
					<Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
						Sales overview
					</Typography>
					<Grid container spacing={2}>
						{[
							{
								label: "Today’s sales",
								value: overviewLoading
									? "—"
									: formatCurrency(todaySales),
								tone: "primary"
							},
							{
								label: "Today’s profit",
								value: maskProfit
									? "••••••"
									: overviewLoading
										? "—"
										: formatCurrency(todayProfit),
								tone: "success"
							},
							{
								label: "Weekly sales",
								value: overviewLoading
									? "—"
									: formatCurrency(weeklySales),
								tone: "primary"
							},
							{
								label: "Outstanding balance",
								value: overviewLoading
									? "—"
									: formatCurrency(totalOutstanding),
								tone: "error"
							}
						].map((metric) => (
							<Grid
								key={metric.label}
								size={{ xs: 12, sm: 6, md: 3 }}
							>
								<Box
									sx={{
										border: 1,
										borderColor: "divider",
										borderRadius: 3,
										p: 2,
										bgcolor: "background.default"
									}}
								>
									<Typography
										variant="caption"
										color="text.secondary"
										sx={{
											textTransform: "uppercase",
											letterSpacing: 1
										}}
									>
										{metric.label}
									</Typography>
									<Typography
										variant="h5"
										color={`${metric.tone}.main`}
										sx={{ mt: 1, fontWeight: 700 }}
									>
										{metric.value}
									</Typography>
								</Box>
							</Grid>
						))}
					</Grid>
				</Paper>

				<Grid container spacing={2} sx={{ mb: 3 }}>
					<Grid size={{ xs: 12, lg: 12 }}>
						<Paper
							sx={{
								borderRadius: 3,
								overflow: "hidden",
								bgcolor: "background.paper"
							}}
						>
							<Box
								sx={{
									p: 2,
									borderBottom: 1,
									borderColor: "divider",
									display: "flex",
									justifyContent: "space-between",
									gap: 2,
									flexWrap: "wrap"
								}}
							>
								<Box>
									<Typography
										variant="subtitle1"
										fontWeight={700}
									>
										Recent sales settlement report
									</Typography>
									<Typography
										variant="body2"
										color="text.secondary"
									>
										Sales and returns in the selected period
									</Typography>
								</Box>
								<Stack
									direction={{ xs: "column", sm: "row" }}
									spacing={1}
								>
									<FormControl sx={{ minWidth: 140 }}>
										<InputLabel>Range</InputLabel>
										<Select
											label="Range"
											value={reportRange}
											onChange={handleReportRangeChange}
										>
											<MenuItem value="today">
												Today
											</MenuItem>
											<MenuItem value="5d">
												Last 5 days
											</MenuItem>
											<MenuItem value="7d">
												Last 7 days
											</MenuItem>
											<MenuItem value="15d">
												Last 15 days
											</MenuItem>
											<MenuItem value="30d">
												Last 30 days
											</MenuItem>
										</Select>
									</FormControl>
									<Button
										variant="outlined"
										onClick={() =>
											navigate("/invoice-and-returns")
										}
									>
										View full report
									</Button>
								</Stack>
							</Box>
							<Box sx={{ p: 2 }}>
								<RecentInvoiceAndReturns
									key={reportRange}
									lastDays={reportDays}
									maskProfit={maskProfit}
								/>
							</Box>
						</Paper>
					</Grid>
				</Grid>

				<Paper
					sx={{
						borderRadius: 3,
						bgcolor: "background.paper",
						overflow: "hidden"
					}}
				>
					<Box
						sx={{
							p: 2,
							borderBottom: 1,
							borderColor: "divider",
							display: "flex",
							justifyContent: "space-between",
							gap: 2,
							flexWrap: "wrap"
						}}
					>
						<Box>
							<Typography variant="subtitle1" fontWeight={700}>
								Outstanding invoices
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Customer balances awaiting payment
							</Typography>
						</Box>
						<Button
							variant="outlined"
							onClick={() => setOutstandingOpen((prev) => !prev)}
						>
							{outstandingOpen ? "Hide" : "Show"}
						</Button>
					</Box>
					<Box sx={{ p: 2 }}>
						<Typography
							variant="body2"
							color="text.secondary"
							sx={{ mb: 1 }}
						>
							Total outstanding balance
						</Typography>
						<Typography
							variant="h5"
							color="error.main"
							sx={{ fontWeight: 700 }}
						>
							₹{totalOutstanding.toFixed(2)}
						</Typography>
						<Collapse in={outstandingOpen} sx={{ mt: 2 }}>
							<CreditInvoices
								getOutstanding={setTotalOutstanding}
							/>
						</Collapse>
					</Box>
				</Paper>
			</Box>

			<ConfirmDialog
				isOpen={logoutConfirmOpen}
				title="Confirm Logout"
				content="Are you sure you want to logout?"
				primaryButtonText="Logout"
				secondaryButtonText="Cancel"
				primaryButtonColor="secondary"
				secondaryButtonColor="info"
				onConfirm={handleLogoutConfirm}
				onCancel={handleLogoutCancel}
			/>

			<Dialog
				open={settingsOpen}
				onClose={() => setSettingsOpen(false)}
				fullWidth
				maxWidth="sm"
			>
				<DialogTitle>Settings</DialogTitle>
				<DialogContent dividers>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							py: 1
						}}
					>
						<Box>
							<Typography fontWeight={700}>Appearance</Typography>
							<Typography variant="body2" color="text.secondary">
								Use dark mode across the dashboard.
							</Typography>
						</Box>
						<Switch
							checked={mode === "dark"}
							onChange={toggleMode}
						/>
					</Box>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							py: 1
						}}
					>
						<Box>
							<Typography fontWeight={700}>
								Mask profit values
							</Typography>
							<Typography variant="body2" color="text.secondary">
								Keep profits hidden on Home until this setting
								is turned off.
							</Typography>
						</Box>
						<Switch
							checked={maskProfit}
							onChange={(_, checked) => {
								void handleMaskProfitToggle(checked);
							}}
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setSettingsOpen(false)}>
						Close
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};
